import { useEffect, useRef, useState } from 'react';
import { Eye, Pencil, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import { useUpdateChapterDraft } from '../hooks/useChapter';
import ChapterView from './ChapterView';
import RichEditor from './editor/RichEditor';

interface Props {
  chapterId: string;
  initialTitle: string;
  initialContent: string;
  initialRequiresAck: boolean;
}

type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export default function ChapterEditor({
  chapterId,
  initialTitle,
  initialContent,
  initialRequiresAck,
}: Props) {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [requiresAck, setRequiresAck] = useState(initialRequiresAck);
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mutation = useUpdateChapterDraft(chapterId);

  useEffect(() => {
    setTitle(initialTitle);
    setContent(initialContent);
    setRequiresAck(initialRequiresAck);
    setStatus('idle');
    setSavedAt(null);
  }, [chapterId, initialTitle, initialContent, initialRequiresAck]);

  // Flag `requires_acknowledgment` ukládáme okamžitě při změně (ne debouncovaně) —
  // je to jednotlivý toggle, očekávané chování je instant.
  const persistAck = (next: boolean) => {
    setRequiresAck(next);
    mutation.mutate({ requires_acknowledgment: next });
  };

  useEffect(() => {
    if (title === initialTitle && content === initialContent) return;
    setStatus('dirty');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setStatus('saving');
      mutation.mutate(
        { title_cs: title, content_cs_md: content },
        {
          onSuccess: () => {
            setStatus('saved');
            setSavedAt(new Date());
          },
          onError: () => setStatus('error'),
        },
      );
    }, 1500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [title, content]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusLabel = (() => {
    switch (status) {
      case 'idle':
        return null;
      case 'dirty':
        return 'Neuložené změny…';
      case 'saving':
        return 'Ukládám…';
      case 'error':
        return 'Ukládání selhalo';
      case 'saved':
        return savedAt
          ? `Uloženo ${savedAt.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`
          : 'Uloženo';
    }
  })();

  return (
    <div>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="mb-4 w-full border-0 bg-transparent font-heading text-4xl font-bold text-dark outline-none placeholder:text-gray-300"
        placeholder="Název kapitoly"
      />

      <label
        className={clsx(
          'mb-4 inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors',
          requiresAck
            ? 'border-gold bg-gold-soft text-dark'
            : 'border-gray-200 bg-white text-gray-500 hover:border-gold hover:text-dark',
        )}
        title="Pokud zapnuto, čtenář musí explicitně potvrdit přečtení — hodí se pro compliance kapitoly."
      >
        <input
          type="checkbox"
          checked={requiresAck}
          onChange={(e) => persistAck(e.target.checked)}
          className="h-3.5 w-3.5 accent-gold"
        />
        <ShieldAlert className={clsx('h-3.5 w-3.5', requiresAck ? 'text-gold' : 'text-gray-400')} />
        Vyžaduje potvrzení přečtení
      </label>

      <div className="mb-2 flex items-center justify-between">
        <div className="flex gap-1 text-sm">
          <TabButton
            active={tab === 'edit'}
            onClick={() => setTab('edit')}
            icon={<Pencil className="h-3.5 w-3.5" />}
          >
            Editace
          </TabButton>
          <TabButton
            active={tab === 'preview'}
            onClick={() => setTab('preview')}
            icon={<Eye className="h-3.5 w-3.5" />}
          >
            Náhled
          </TabButton>
        </div>
        {statusLabel && (
          <div
            className={clsx(
              'text-xs',
              status === 'error' && 'text-danger',
              status === 'saved' && 'text-gray-500',
              (status === 'saving' || status === 'dirty') && 'text-gray-400',
            )}
          >
            {statusLabel}
          </div>
        )}
      </div>

      {tab === 'edit' ? (
        <RichEditor
          chapterId={chapterId}
          initialMarkdown={content}
          onChange={setContent}
          placeholder="Piš obsah kapitoly…"
        />
      ) : (
        <ChapterView markdown={content} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
        active ? 'bg-gray-100 text-dark' : 'text-gray-500 hover:text-dark',
      )}
    >
      {icon}
      {children}
    </button>
  );
}
